import qs from 'qs';

import { actions, definitions } from '@luxuryescapes/contract-svc-accommodation';

import { json_headers, request } from './common';
import { HttpResponse } from './types';

export interface AttachedPromotionSchedule {
  activeStartDate: string | null;
  activeEndDate: string | null;
}

export interface AttachedBundle {
  id: string;
  name: string;
  status: 'draft' | 'content-approved';
  isActive: boolean;
  schedules: Array<AttachedPromotionSchedule>;
}

export interface AttachedCampaign {
  id: string;
  displayName: string;
  status: 'draft' | 'active';
  schedules: Array<AttachedPromotionSchedule>;
}

export type PropertyResponse = definitions['AdminProperty'];
export type Room = definitions['AdminRoomDetail'];
export type PropertyDetailResponse = definitions['AdminPropertyDetail'];
export type AccommodationBookingDetails = definitions['BookingDetails'];
export type AccommodationBookingDetailsQuery = actions['getBookingDetails']['parameters']['query'];
export type AccommodationBookingDetailsResponse =
  actions['getBookingDetails']['responses']['200']['content']['application/json'];
export type PropertyContentResponse = definitions['PropertyContent'];
export type AdminOfferContentResponse = definitions['AdminOfferContent'];
export type AdminPropertyContentResponse = definitions['AdminPropertyContent'];
export type ProgressStatusResponse = definitions['StatusResponse'];
export type ProgressStatusResponseMeta = Extract<definitions['StatusResponse']['meta'], { propertyId?: string }>;
export type MappedInternalProperty = definitions['AdminMappedInternalProperty'];
export type PropertyMappingMethod = definitions['PropertyMappingMethodEnum'];
export type MappedInternalPropertyWithOffers = definitions['AdminMappedInternalPropertyWithOffers'];
export type InternalProperty = definitions['AdminInternalProperty'];
export type InternalService = definitions['InternalService'];
export type AdminPropertyImages = definitions['AdminPropertyImages'];
export type AdminRoomImages = definitions['AdminRoomImages'];
export type RoomVisibility = definitions['AdminRoom']['visibility'];

export type RoomResponse = definitions['AdminRoom'];
export type PatchPropertyPayload = actions['patchAdminProperty']['requestBody']['content']['application/json'];
export type PatchRoomPayload = actions['patchAdminRoom']['requestBody']['content']['application/json'];
export type InternalRoom = definitions['AdminInternalRoom'];
export type SupplierRoom = definitions['AdminSupplierRoom'];
export type MappedInternalRoom = definitions['AdminMappedInternalRoom'];
export type SupplierRoomsForInternalRoomResponse = definitions['AdminSupplierRoomsForInternalRoom'];
export type RoomMappingResponse = definitions['AdminPropertyRoomMappings'];
export type RoomMapping = definitions['AdminRoomMapping'];
export type RoomAmenitiesMapping = definitions['AdminRoomAmenitiesMappings'];
export type PropertyFacilitiesMapping = definitions['AdminPropertyFacilitiesMappings'];
export type ExtendedMappedInternalRoom = MappedInternalRoom & { roomId: string };
export type RoomMappingPayload = actions['postAdminPropertyRoomMappings']['requestBody']['content']['application/json'];
export type ValidationErrorCodes = definitions['ValidationErrorCodes'];
export type ValidationErrorDetails = definitions['ValidationErrorDetails'];
export type VerifyMappedRoomPayload =
  actions['postAdminVerifyRoomMappings']['requestBody']['content']['application/json'];
export type SourceModel = definitions['SourceModel'];
export type TargetModel = definitions['TargetModel'];
export type MappingLog = definitions['AdminMappingLog'];
export type VerificationStatusEnum = definitions['VerificationStatusEnum'];
export type PotentialMappingPropertyDetail = definitions['AdminPotentialMappingPropertyDetail'];
export type PotentialMappingDetails = definitions['AdminPotentialMappingDetails'];
export type PropertyStateEnum = definitions['PropertyStateEnum'];
export type AvailabilityResponse =
  actions['unifiedAvailability']['responses']['200']['content']['application/json']['result'];
export type AvailabilityDebugResponse =
  actions['adminUnifiedAvailabilityDebug']['responses']['200']['content']['application/json']['result'];
export type DebugInfo = AvailabilityDebugResponse['debug'];
export type RemovedRate = definitions['RemovedRate'];
export type PricingIssue = definitions['PricingIssue'];
export type MergedRate = definitions['MergedRate'];
export type RoomRates = AvailabilityResponse['properties'][string]['rooms'];
export type RoomRate = RoomRates[string];
export type Rate = definitions['UnifiedRate'];
export type PriceAdjustment = definitions['PriceAdjustment'];
export type AdminPrompt = definitions['AdminPrompt'];
export type AdminOfferPromptResponse = AdminPrompt;
export type PropertyDebugResponse = definitions['AdminPropertyDebug'];
export type RefreshedPropertyReviews = definitions['RefreshedPropertyReviews'];
export type PreviewProdOfferResponse = definitions['PreviewProdOfferResult'];
export type PreviewCloneOfferResponse = definitions['PreviewCloneOfferResult'];
export type MakeMeAnOfferStepStatus = Extract<ProgressStatusResponse['meta'], { createOffer?: unknown }>['createOffer'];

export type MakeMeAnOfferActionType =
  | 'upsert-prod-flash-offer'
  | 'upsert-prod-lpc-offer'
  | 'clone-flash-offer'
  | 'clone-lpc-offer';

export type MakeMeAnOfferRegularMeta = Extract<ProgressStatusResponse['meta'], { createOffer?: unknown }>;

export type MakeMeAnOfferUpsertFlashMeta = Extract<
  ProgressStatusResponse['meta'],
  { actionType: 'upsert-prod-flash-offer' }
>;
export type MakeMeAnOfferUpsertLpcMeta = Extract<
  ProgressStatusResponse['meta'],
  { actionType: 'upsert-prod-lpc-offer' }
>;
export type MakeMeAnOfferCloneFlashMeta = Extract<ProgressStatusResponse['meta'], { actionType: 'clone-flash-offer' }>;
export type MakeMeAnOfferCloneLpcMeta = Extract<ProgressStatusResponse['meta'], { actionType: 'clone-lpc-offer' }>;

// Error meta
export type MakeMeAnOfferErrorMeta = Extract<ProgressStatusResponse['meta'], { error?: string }>;
export type AdminFacility = definitions['AdminFacility'];
export type AdminCatalogGroup = definitions['AdminCatalogGroup'];
export type AdminPropertyFacility = definitions['AdminPropertyFacility'];
export type StructuredPropertyFacilitiesMapping = definitions['AdminExportPropertyFacilitiesMappings'];
export type PropertyFacilityAddPayload =
  actions['addAdminPropertyFacility']['requestBody']['content']['application/json'];
export type PropertyFacilityUpdatePayload =
  actions['updateAdminPropertyFacility']['requestBody']['content']['application/json'];
export type ApproveFacilityPayload = actions['approveDraftFacility']['requestBody']['content']['application/json'];
export type MergeFacilityPayload = actions['mergeFacility']['requestBody']['content']['application/json'];
export type CatalogGroupIcon = definitions['AdminCatalogGroup']['icon'];
export type Icon = definitions['Icon'];
export type ListIconsResponse = actions['listAdminIcons']['responses']['200']['content']['application/json']['result'];
export type AdminAmenity = definitions['AdminAmenity'];
export type AdminRoomCatalogAmenity = definitions['AdminRoomCatalogAmenity'];
export type RoomAmenityAddPayload = actions['addAdminRoomAmenity']['requestBody']['content']['application/json'];
export type RoomAmenityUpdatePayload = actions['updateAdminRoomAmenity']['requestBody']['content']['application/json'];
export type ApproveAmenityPayload = actions['approveDraftAmenity']['requestBody']['content']['application/json'];
export type MergeAmenityPayload = actions['mergeAmenity']['requestBody']['content']['application/json'];
export type AdminInclusion = definitions['AdminCatalogInclusion'];
export type InclusionCreatePayload = actions['createAdminInclusion']['requestBody']['content']['application/json'];
export type InclusionUpdatePayload = actions['updateAdminInclusion']['requestBody']['content']['application/json'];
export type ApproveInclusionPayload = actions['approveDraftInclusion']['requestBody']['content']['application/json'];
export type MergeInclusionPayload = actions['mergeInclusion']['requestBody']['content']['application/json'];
export type MmaoOffer = definitions['AdminMmaoOffer'];
export type ListMmaoOffersQuery = actions['listAdminMmaoOffers']['parameters']['query'];

export type ContentSourceInfo = definitions['ContentSourceInfo'];
export type ContentSourceFieldSelection = definitions['ContentSourceFieldSelection'];
export type PropertyContentSourcesResponse =
  actions['getPropertyContentSources']['responses']['200']['content']['application/json']['result'];
export type RoomContentSourcesResponse =
  actions['getRoomContentSources']['responses']['200']['content']['application/json']['result'];
export type UpdatePropertyContentSourcesPayload =
  actions['updatePropertyContentSources']['requestBody']['content']['application/json'];
export type BulkUpdatePropertyContentSourcesPayload =
  actions['bulkUpdatePropertyContentSources']['requestBody']['content']['application/json'];
export type UpdateRoomContentSourcesPayload =
  actions['updateRoomContentSources']['requestBody']['content']['application/json'];

export function basePath() {
  return window.configs.LUX_API_DOMAIN + '/api/accommodation';
}

function baseAdminPath() {
  return window.configs.LUX_API_DOMAIN + '/api/accommodation/admin';
}

/**
 * Full accommodation booking details, looked up by bookingId, orderId or orderItemId. The
 * route is customer-accessible, but an admin bearer token is privileged, so the response
 * includes the internal status, supplier references and the as-sold rate snapshot.
 */
export const getBookingDetails = (
  query: AccommodationBookingDetailsQuery,
  options?: { signal?: AbortSignal },
): Promise<AccommodationBookingDetailsResponse> => {
  const url = `${basePath()}/bookings/details?${qs.stringify(query)}`;
  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
};

type ListPropertiesQuery = actions['listAdminProperties']['parameters']['query'];

export const listProperties = ({
  name,
  id,
  city,
  country,
  categories,
  rating,
  limit,
  page,
  propertyState,
  hasApprovedFlashOffer,
  hasApprovedLpcOffer,
  hasBedbankRatesEnabled,
  hasBedbankRoomsMapped,
  hasFlashOffer,
  hasLpcOffer,
  hasLpcRatesEnabled,
  hasLpcRoomsMapped,
  hasScheduledFlashOffer,
  hasScheduledLpcOffer,
  hasUltraLuxSupplier,
  hasBundle,
  hasCampaign,
  sortBy,
  sortOrder,
}: ListPropertiesQuery): Promise<HttpResponse<{ total: number; properties: Array<PropertyResponse> }>> => {
  const url = `${baseAdminPath()}/properties?${qs.stringify({
    name,
    id,
    city,
    country,
    categories,
    rating,
    limit,
    page,
    propertyState,
    hasApprovedFlashOffer,
    hasApprovedLpcOffer,
    hasBedbankRatesEnabled,
    hasBedbankRoomsMapped,
    hasFlashOffer,
    hasLpcOffer,
    hasLpcRatesEnabled,
    hasLpcRoomsMapped,
    hasScheduledFlashOffer,
    hasScheduledLpcOffer,
    hasUltraLuxSupplier,
    hasBundle,
    hasCampaign,
    sortBy,
    sortOrder,
  })}`;
  return request(url, {
    method: `GET`,
  });
};

export const listMmaoOffers = ({
  offerId,
  createdBy,
  updatedAtFrom,
  updatedAtTo,
  limit,
  page,
}: ListMmaoOffersQuery): Promise<HttpResponse<{ total: number; mmaoOffers: Array<MmaoOffer> }>> => {
  const url = `${baseAdminPath()}/mmao-offers?${qs.stringify({
    offerId,
    createdBy,
    updatedAtFrom,
    updatedAtTo,
    limit,
    page,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export const getDynamicConfigurations = ({
  signal,
}: {
  signal?: AbortSignal;
} = {}): Promise<HttpResponse<Record<string, unknown>>> => {
  const url = `${basePath()}/dynamic-configs`;
  return request(url, {
    method: 'GET',
    signal,
  });
};

export type PropertyMapping = definitions['PropertyMapping'];

export const getPropertyMappings = (
  query: actions['propertyMappings']['parameters']['query'],
  options?: { signal?: AbortSignal },
): Promise<HttpResponse<PropertyMapping>> => {
  const url = `${basePath()}/properties/mappings?${qs.stringify(query)}`;
  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
};

export const getProperty = (
  { propertyId }: actions['getAdminProperty']['parameters']['path'],
  options?: { signal?: AbortSignal },
): Promise<HttpResponse<PropertyResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}`;
  return request(url, {
    method: `GET`,
    signal: options?.signal,
  });
};

export const patchProperty = (
  { propertyId }: actions['patchAdminProperty']['parameters']['path'],
  data: actions['patchAdminProperty']['requestBody']['content']['application/json'],
): Promise<HttpResponse<PropertyResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const createProperty = ({
  internalPropertyId,
}: actions['createAdminProperty']['requestBody']['content']['application/json']): Promise<
  HttpResponse<ProgressStatusResponse>
> => {
  const url = `${baseAdminPath()}/properties`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ internalPropertyId }),
  });
};

export const listMappedInternalProperties = ({
  propertyId,
  verificationStatuses,
}: actions['getAdminInternalProperties']['parameters']['path'] & {
  verificationStatuses: Array<VerificationStatusEnum>;
}): Promise<HttpResponse<Array<MappedInternalPropertyWithOffers>>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/internal-properties?${qs.stringify({
    verificationStatuses,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export const getInternalProperty = ({
  internalService,
  internalServiceId,
}: actions['getAdminInternalProperty']['parameters']['path']): Promise<HttpResponse<InternalProperty>> => {
  const url = `${baseAdminPath()}/internalService/${internalService}/internal-properties/${internalServiceId}`;
  return request(url, {
    method: 'GET',
  });
};

export const getUnmappedSuppliers = ({
  name,
  id,
  internalService,
  limit,
  page,
  verificationStatuses,
}: actions['getAdminUnmappedInternalProperties']['parameters']['query']): Promise<
  HttpResponse<{ total: number; internalProperties: Array<InternalProperty> }>
> => {
  const url = `${baseAdminPath()}/properties/unmapped-internal-properties?${qs.stringify({
    name,
    id,
    internalService,
    limit,
    page,
    verificationStatuses,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export const patchInternalProperty = (
  { propertyId, internalPropertyId }: actions['patchAdminInternalProperty']['parameters']['path'],
  data: actions['patchAdminInternalProperty']['requestBody']['content']['application/json'],
): Promise<HttpResponse<InternalProperty>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/internal-properties/${internalPropertyId}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const listRooms = ({
  propertyId,
  signal,
}: actions['getAdminRooms']['parameters']['path'] & {
  signal?: AbortSignal;
}): Promise<HttpResponse<Array<RoomResponse>>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms`;
  return request(url, {
    method: 'GET',
    signal,
  });
};

export const patchRoom = (
  { propertyId, roomId }: actions['patchAdminRoom']['parameters']['path'],
  data: PatchRoomPayload,
): Promise<HttpResponse<RoomResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/${roomId}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const getProgressStatuses = (id: string): Promise<HttpResponse<Array<ProgressStatusResponse>>> => {
  const url = `${basePath()}/progress/statuses?${qs.stringify({ id })}`;
  return request(url, {
    method: `GET`,
  });
};

export const getRoomMappings = (propertyId: string): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/mappings`;
  return request(url, {
    method: `GET`,
  });
};

export const getSupplierRoomsForInternalRoom = ({
  internalRoomId,
  signal,
}: {
  internalRoomId: string;
  signal?: AbortSignal;
}): Promise<HttpResponse<SupplierRoomsForInternalRoomResponse>> => {
  const url = `${baseAdminPath()}/rooms/${internalRoomId}/supplier-rooms`;
  return request(url, {
    method: 'GET',
    signal,
  });
};

export const postRoomMappings = (
  propertyId: string,
  mappings: RoomMappingPayload,
): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/mappings`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify(mappings),
  });
};

export const deleteRoomMappings = (
  propertyId: string,
  mappings: RoomMappingPayload,
): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/mappings`;
  return request(url, {
    method: `DELETE`,
    headers: json_headers,
    body: JSON.stringify(mappings),
  });
};

export const createAndMapRoom = (
  propertyId: string,
  internalRoomIds: Array<string>,
): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/create-and-map`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify({ internalRoomIds }),
  });
};

export const verifyMappedRooms = (
  propertyId: string,
  payload: VerifyMappedRoomPayload,
): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/verify-mappings`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const getMappingLogs = ({
  sourceModel,
  sourceId,
  targetModel,
  targetId,
}: actions['getMappingLogs']['parameters']['query']): Promise<HttpResponse<Array<definitions['AdminMappingLog']>>> => {
  const url = `${baseAdminPath()}/mapping-logs?${qs.stringify({ sourceModel, sourceId, targetModel, targetId })}`;
  return request(url, {
    method: `GET`,
  });
};

export const mapInternalProperty = ({
  propertyId,
  internalPropertyId,
  validationOverrides,
}: actions['mapInternalProperty']['parameters']['path'] &
  actions['mapInternalProperty']['requestBody']['content']['application/json']): Promise<HttpResponse<object>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/internal-properties/${internalPropertyId}`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify({ validationOverrides }),
  });
};

export const unmapInternalProperty = ({
  propertyId,
  internalPropertyId,
}: actions['unmapInternalProperty']['parameters']['path']): Promise<HttpResponse<object>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/internal-properties/${internalPropertyId}`;
  return request(url, {
    method: `DELETE`,
  });
};

export const deleteProperty = (propertyId: string): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}`;
  return request(url, {
    method: 'DELETE',
  });
};

export const getPotentialMappings = ({
  name,
  id,
  country,
  internalService,
  chainNames,
  excludedChainNames,
  mappingMethods,
  verificationStatuses,
  page,
  limit,
}: Omit<
  actions['getPotentialMappings']['parameters']['query'],
  'chainNames' | 'excludedChainNames' | 'mappingMethods' | 'verificationStatuses'
> & {
  country?: string;
  chainNames?: Array<string>;
  excludedChainNames?: Array<string>;
  mappingMethods?: Array<PropertyMappingMethod>;
  verificationStatuses?: Array<string>;
}): Promise<HttpResponse<{ total: number; mappedProperties: Array<MappedInternalProperty> }>> => {
  const url = `${baseAdminPath()}/properties/potential-mappings?${qs.stringify({
    name,
    id,
    country,
    internalService,
    chainNames: chainNames?.join(','),
    excludedChainNames: excludedChainNames?.join(','),
    mappingMethods: mappingMethods?.length ? mappingMethods.join(',') : undefined,
    verificationStatuses: verificationStatuses?.length ? verificationStatuses.join(',') : undefined,
    page,
    limit,
  })}`;
  return request(url, {
    method: `GET`,
  });
};

export const getPotentialMappingDetails = ({
  propertyId,
  mappedPropertyId,
}: actions['getMappedPropertyDetails']['parameters']['path']): Promise<HttpResponse<PotentialMappingDetails>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/mapped-properties/${mappedPropertyId}/details`;
  return request(url, {
    method: `GET`,
  });
};

export interface UpdateMappingPayload {
  propertyId: string;
  mappedPropertyId: string;
  verificationStatus: VerificationStatusEnum;
  verifiedComments?: string;
}

export const updateMapping = ({
  propertyId,
  mappedPropertyId,
  verificationStatus,
  verifiedComments,
}: UpdateMappingPayload): Promise<HttpResponse<definitions['AdminMappedInternalProperty']>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/mapped-properties/${mappedPropertyId}`;
  return request(url, {
    method: `PATCH`,
    headers: json_headers,
    body: JSON.stringify({ verificationStatus, verifiedComments }),
  });
};

export const reimportSuppliers = (propertyId: string): Promise<HttpResponse<ProgressStatusResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/suppliers/reimport`;
  return request(url, {
    method: `POST`,
  });
};

export async function deleteRoom({
  propertyId,
  roomId,
}: actions['deleteAdminRoom']['parameters']['path']): Promise<HttpResponse<void>> {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/${roomId}`;
  return request(url, {
    method: 'DELETE',
  });
}
export async function initiateOfferContentGeneration({
  offerId,
  creativity,
  contentType,
  includeOfferInGeneration,
  useWebSearch,
}: actions['generateOfferContent']['parameters']['path'] &
  Omit<actions['generateOfferContent']['requestBody']['content']['application/json'], 'contentType'> & {
    contentType?: 'description' | 'nearby' | 'finePrint';
  }): Promise<HttpResponse<ProgressStatusResponse>> {
  const url = `${baseAdminPath()}/content/offers/${offerId}/generate-content`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify({
      creativity,
      contentType,
      includeOfferInGeneration,
      useWebSearch,
    }),
  });
}

export const getAvailability = ({
  checkIn,
  checkOut,
  occupancies,
  propertyIds,
  region,
  currency,
  brand,
  allRates,
  dynamicAvailability,
  allowMysteryRooms,
  useV2,
  source,
}: actions['unifiedAvailability']['parameters']['query']): Promise<HttpResponse<AvailabilityResponse>> => {
  const url = `${basePath()}/properties/availability?${qs.stringify({
    checkIn,
    checkOut,
    occupancies,
    propertyIds,
    region,
    currency,
    brand,
    allRates,
    dynamicAvailability,
    allowMysteryRooms,
    useV2,
    source,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export const getAvailabilityDebug = ({
  propertyIds,
  checkIn,
  checkOut,
  occupancies,
  region,
  currency,
  brand,
  allRates,
  dynamicAvailability,
  allowMysteryRooms,
  useV2,
  source,
  forceCapabilities,
  useE2eMockRates,
}: actions['adminUnifiedAvailabilityDebug']['parameters']['query']): Promise<
  HttpResponse<AvailabilityDebugResponse>
> => {
  const url = `${basePath()}/admin/properties/availability/debug?${qs.stringify({
    propertyIds,
    checkIn,
    checkOut,
    occupancies,
    region,
    currency,
    brand,
    allRates,
    dynamicAvailability,
    allowMysteryRooms,
    useV2,
    source,
    forceCapabilities,
    useE2eMockRates,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export async function getPropertyContent({
  propertyId,
}: actions['getAdminPropertyContent']['parameters']['path']): Promise<HttpResponse<AdminPropertyContentResponse>> {
  const url = `${baseAdminPath()}/content/properties/${propertyId}`;
  return request(url, {
    method: `GET`,
  });
}

export const getOfferContent = (
  { offerId }: actions['getAdminOfferContent']['parameters']['path'],
  options?: { signal?: AbortSignal },
): Promise<HttpResponse<AdminOfferContentResponse>> => {
  const url = `${baseAdminPath()}/content/offers/${offerId}`;
  return request(url, {
    method: `GET`,
    signal: options?.signal,
  });
};

export const getOfferPrompt = (): Promise<HttpResponse<AdminOfferPromptResponse>> => {
  const url = `${baseAdminPath()}/content/offers/get-prompt`;
  return request(url, {
    method: `GET`,
  });
};

export const getPropertyDebug = ({
  propertyId,
}: actions['getAdminPropertyDebug']['parameters']['path']): Promise<HttpResponse<PropertyDebugResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/debug`;
  return request(url, {
    method: `GET`,
  });
};

export const refreshPropertyDebug = ({
  propertyId,
}: actions['refreshAdminPropertyDebug']['parameters']['path']): Promise<HttpResponse<PropertyDebugResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/debug/refresh`;
  return request(url, {
    method: `POST`,
  });
};

export const refreshAllPropertyContentCache = ({
  propertyId,
}: actions['refreshAllAdminPropertyReviews']['parameters']['path']): Promise<
  HttpResponse<Array<RefreshedPropertyReviews>>
> => {
  const url = `${baseAdminPath()}/content/properties/${propertyId}/reviews/refresh-all`;
  return request(url, {
    method: `POST`,
  });
};

export async function previewProdOffer(offerId: string): Promise<PreviewProdOfferResponse> {
  const url = `${baseAdminPath()}/preview-prod-offer`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ offerId }),
  });
}

export async function previewCloneOffer(offerId: string): Promise<PreviewCloneOfferResponse> {
  const url = `${baseAdminPath()}/preview-clone-offer`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ offerId }),
  });
}

type MakeMeAnOfferRequest = actions['makeTestOffer']['requestBody']['content']['application/json'];
type MakeMeAnOfferResponse = actions['makeTestOffer']['responses']['200']['content']['application/json'];

export async function makeMeAnOffer(payload: MakeMeAnOfferRequest): Promise<MakeMeAnOfferResponse> {
  const url = `${baseAdminPath()}/make-me-a-test-offer`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
}

export async function syncRoomAmenities({
  reservationPropertyId,
  reservationRoomId,
}: {
  reservationPropertyId: string;
  reservationRoomId: string;
}): Promise<HttpResponse<RoomAmenitiesMapping>> {
  const url = `${baseAdminPath()}/room-amenities?reservationPropertyId=${reservationPropertyId}&reservationRoomTypeId=${reservationRoomId}`;
  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

export async function syncPropertyFacilities({
  propertyId,
}: {
  propertyId: string;
}): Promise<HttpResponse<PropertyFacilitiesMapping>> {
  const url = `${baseAdminPath()}/property-facilities?reservationPropertyId=${propertyId}`;
  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

export async function exportPropertyFacilities({
  propertyId,
}: {
  propertyId: string;
}): Promise<HttpResponse<StructuredPropertyFacilitiesMapping>> {
  const url = `${baseAdminPath()}/property-facilities/export?reservationPropertyId=${propertyId}`;
  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

export const listFacilities = ({
  name,
  catalogGroupId,
  status,
  limit,
  page,
}: actions['listAdminFacilities']['parameters']['query']): Promise<
  HttpResponse<{ facilities: Array<AdminFacility>; total: number }>
> => {
  const url = `${baseAdminPath()}/content/facilities?${qs.stringify({
    name,
    catalogGroupId,
    status,
    limit,
    page,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export type FacilityUpdatePayload = actions['updateAdminFacility']['requestBody']['content']['application/json'];
export const updateFacility = (
  { code }: actions['updateAdminFacility']['parameters']['path'],
  data: FacilityUpdatePayload,
): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/${code}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const deleteFacility = ({
  code,
}: actions['deleteAdminFacility']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/facilities/${code}`;
  return request(url, {
    method: 'DELETE',
  });
};

export type FacilityCreatePayload = actions['createAdminFacility']['requestBody']['content']['application/json'];
export const createFacility = (data: FacilityCreatePayload): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const listCatalogGroups = ({
  name,
  type,
  limit,
  page,
}: actions['listAdminCatalogGroups']['parameters']['query']): Promise<
  HttpResponse<{ groups: Array<AdminCatalogGroup>; total: number }>
> => {
  const url = `${baseAdminPath()}/catalog-groups?${qs.stringify({
    name,
    type,
    limit,
    page,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export type CatalogGroupCreatePayload =
  actions['createAdminCatalogGroup']['requestBody']['content']['application/json'];
export const createCatalogGroup = (data: CatalogGroupCreatePayload): Promise<HttpResponse<AdminCatalogGroup>> => {
  const url = `${baseAdminPath()}/catalog-groups`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export type CatalogGroupUpdatePayload =
  actions['updateAdminCatalogGroup']['requestBody']['content']['application/json'];
export const updateCatalogGroup = (
  { id }: actions['updateAdminCatalogGroup']['parameters']['path'],
  data: CatalogGroupUpdatePayload,
): Promise<HttpResponse<AdminCatalogGroup>> => {
  const url = `${baseAdminPath()}/catalog-groups/${id}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const deleteCatalogGroup = ({
  id,
}: actions['deleteAdminCatalogGroup']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/catalog-groups/${id}`;
  return request(url, {
    method: 'DELETE',
  });
};

// Property Facilities
export const listPropertyFacilities = (
  { propertyId }: actions['listAdminPropertyFacilities']['parameters']['path'],
  query: actions['listAdminPropertyFacilities']['parameters']['query'],
): Promise<HttpResponse<{ facilities: Array<AdminPropertyFacility>; total: number }>> => {
  const url = `${baseAdminPath()}/content/facilities/properties/${propertyId}?${qs.stringify(query)}`;
  return request(url, {
    method: 'GET',
  });
};

export const addPropertyFacility = (
  { propertyId }: actions['addAdminPropertyFacility']['parameters']['path'],
  data: PropertyFacilityAddPayload,
): Promise<HttpResponse<AdminPropertyFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/properties/${propertyId}`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const updatePropertyFacility = (
  { propertyId, facilityCode }: actions['updateAdminPropertyFacility']['parameters']['path'],
  data: PropertyFacilityUpdatePayload,
): Promise<HttpResponse<AdminPropertyFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/properties/${propertyId}/${facilityCode}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const removePropertyFacility = ({
  propertyId,
  facilityCode,
}: actions['removeAdminPropertyFacility']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/facilities/properties/${propertyId}/${facilityCode}`;
  return request(url, {
    method: 'DELETE',
  });
};

// Draft Facility Operations
export const approveFacility = (
  { code }: actions['approveDraftFacility']['parameters']['path'],
  data: ApproveFacilityPayload,
): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/${code}/approve`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const discardFacility = ({
  code,
}: actions['discardDraftFacility']['parameters']['path']): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/${code}/discard`;
  return request(url, {
    method: 'POST',
  });
};

export const mergeFacility = (data: MergeFacilityPayload): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/merge`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

// Icons
export const listIcons = (): Promise<HttpResponse<ListIconsResponse>> => {
  const url = `${baseAdminPath()}/content/icons`;
  return request(url, {
    method: 'GET',
  });
};

// Amenities
export const listAmenities = ({
  name,
  catalogGroupId,
  status,
  limit,
  page,
}: actions['listAdminAmenities']['parameters']['query']): Promise<
  HttpResponse<{ amenities: Array<AdminAmenity>; total: number }>
> => {
  const url = `${baseAdminPath()}/content/amenities?${qs.stringify({
    name,
    catalogGroupId,
    status,
    limit,
    page,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export type AmenityUpdatePayload = actions['updateAdminAmenity']['requestBody']['content']['application/json'];
export const updateAmenity = (
  { code }: actions['updateAdminAmenity']['parameters']['path'],
  data: AmenityUpdatePayload,
): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/${code}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const deleteAmenity = ({
  code,
}: actions['deleteAdminAmenity']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/amenities/${code}`;
  return request(url, {
    method: 'DELETE',
  });
};

export type AmenityCreatePayload = actions['createAdminAmenity']['requestBody']['content']['application/json'];
export const createAmenity = (data: AmenityCreatePayload): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

// Property Amenities
export const listPropertyAmenities = (
  { roomId }: actions['listAdminRoomAmenities']['parameters']['path'],
  query: actions['listAdminRoomAmenities']['parameters']['query'],
): Promise<HttpResponse<{ amenities: Array<AdminRoomCatalogAmenity>; total: number }>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}?${qs.stringify(query)}`;
  return request(url, {
    method: 'GET',
  });
};

export const addPropertyAmenity = (
  { roomId }: actions['addAdminRoomAmenity']['parameters']['path'],
  data: RoomAmenityAddPayload,
): Promise<HttpResponse<AdminRoomCatalogAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const updatePropertyAmenity = (
  { roomId, amenityCode }: actions['updateAdminRoomAmenity']['parameters']['path'],
  data: RoomAmenityUpdatePayload,
): Promise<HttpResponse<AdminRoomCatalogAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}/${amenityCode}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const removePropertyAmenity = ({
  roomId,
  amenityCode,
}: actions['removeAdminRoomAmenity']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}/${amenityCode}`;
  return request(url, {
    method: 'DELETE',
  });
};

// Draft Amenity Operations
export const approveAmenity = (
  { code }: actions['approveDraftAmenity']['parameters']['path'],
  data: ApproveAmenityPayload,
): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/${code}/approve`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const discardAmenity = ({
  code,
}: actions['discardDraftAmenity']['parameters']['path']): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/${code}/discard`;
  return request(url, {
    method: 'POST',
  });
};

export const mergeAmenity = (data: MergeAmenityPayload): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/merge`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

// Inclusions
export const listInclusions = ({
  name,
  catalogGroupId,
  status,
  limit,
  page,
}: actions['listAdminInclusions']['parameters']['query']): Promise<
  HttpResponse<{ inclusions: Array<AdminInclusion>; total: number }>
> => {
  const url = `${baseAdminPath()}/content/inclusions?${qs.stringify({
    name,
    catalogGroupId,
    status,
    limit,
    page,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export const updateInclusion = (
  { code }: actions['updateAdminInclusion']['parameters']['path'],
  data: InclusionUpdatePayload,
): Promise<HttpResponse<AdminInclusion>> => {
  const url = `${baseAdminPath()}/content/inclusions/${code}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const deleteInclusion = ({
  code,
}: actions['deleteAdminInclusion']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/inclusions/${code}`;
  return request(url, {
    method: 'DELETE',
  });
};

export const createInclusion = (data: InclusionCreatePayload): Promise<HttpResponse<AdminInclusion>> => {
  const url = `${baseAdminPath()}/content/inclusions`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const approveInclusion = (
  { code }: actions['approveDraftInclusion']['parameters']['path'],
  data: ApproveInclusionPayload,
): Promise<HttpResponse<AdminInclusion>> => {
  const url = `${baseAdminPath()}/content/inclusions/${code}/approve`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const discardInclusion = ({
  code,
}: actions['discardDraftInclusion']['parameters']['path']): Promise<HttpResponse<AdminInclusion>> => {
  const url = `${baseAdminPath()}/content/inclusions/${code}/discard`;
  return request(url, {
    method: 'POST',
  });
};

export const mergeInclusion = (data: MergeInclusionPayload): Promise<HttpResponse<AdminInclusion>> => {
  const url = `${baseAdminPath()}/content/inclusions/merge`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

// Room Amenities
export const listRoomAmenities = (
  { roomId }: { roomId: string },
  query: actions['listAdminRoomAmenities']['parameters']['query'],
): Promise<HttpResponse<{ amenities: Array<AdminRoomCatalogAmenity>; total: number }>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}?${qs.stringify(query)}`;
  return request(url, {
    method: 'GET',
  });
};

export const addRoomAmenity = (
  { roomId }: { roomId: string },
  data: RoomAmenityAddPayload,
): Promise<HttpResponse<AdminRoomCatalogAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const updateRoomAmenity = (
  { roomId, amenityCode }: { roomId: string; amenityCode: string },
  data: RoomAmenityUpdatePayload,
): Promise<HttpResponse<AdminRoomCatalogAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}/${amenityCode}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const removeRoomAmenity = ({
  roomId,
  amenityCode,
}: {
  roomId: string;
  amenityCode: string;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}/${amenityCode}`;
  return request(url, {
    method: 'DELETE',
  });
};

type GetAdminRoomImagesResponse = actions['getAdminRoomImages']['responses']['200']['content']['application/json'];
type GetAdminRoomImagesResult = GetAdminRoomImagesResponse['result'];
type GetAdminPropertyImagesResponse =
  actions['getAdminPropertyImages']['responses']['200']['content']['application/json'];
type GetAdminPropertyImagesResult = GetAdminPropertyImagesResponse['result'];

export type RoomImagesResponse = GetAdminRoomImagesResult;
export type AdminRoomImageResponse = RoomImagesResponse['images'][number];
export type UpdateRoomImagesPayload = actions['updateAdminRoomImages']['requestBody']['content']['application/json'];
export type UpdateRoomImageInput = UpdateRoomImagesPayload['images'][number];

export type PropertyImagesResponse = GetAdminPropertyImagesResult;
export type DedupePropertyImagesResponse = { id: string; type: 'imageDedupe' };
export type AdminPropertyImageResponse = PropertyImagesResponse['images'][number];
export type UpdatePropertyImagesPayload =
  actions['updateAdminPropertyImages']['requestBody']['content']['application/json'];
export type UpdatePropertyImageInput = UpdatePropertyImagesPayload['images'][number];

export const getPropertyContentSources = (
  { propertyId }: actions['getPropertyContentSources']['parameters']['path'],
  options?: { signal?: AbortSignal },
): Promise<HttpResponse<PropertyContentSourcesResponse>> => {
  const url = `${baseAdminPath()}/content/properties/${propertyId}/sources`;
  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
};

export const updatePropertyContentSources = (
  { propertyId }: actions['updatePropertyContentSources']['parameters']['path'],
  data: UpdatePropertyContentSourcesPayload,
): Promise<HttpResponse<PropertyContentSourcesResponse>> => {
  const url = `${baseAdminPath()}/content/properties/${propertyId}/sources`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const getRoomImages = ({
  roomId,
  signal,
}: {
  roomId: string;
  signal?: AbortSignal;
}): Promise<HttpResponse<RoomImagesResponse>> => {
  const url = `${baseAdminPath()}/content/rooms/${roomId}/images`;
  return request(url, {
    method: 'GET',
    signal,
  });
};

export const updateRoomImages = ({
  roomId,
  images,
}: {
  roomId: string;
} & UpdateRoomImagesPayload): Promise<HttpResponse<RoomImagesResponse>> => {
  const url = `${baseAdminPath()}/content/rooms/${roomId}/images`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ images }),
  });
};

export const getPropertyImages = ({
  propertyId,
  signal,
}: {
  propertyId: string;
  signal?: AbortSignal;
}): Promise<HttpResponse<PropertyImagesResponse>> => {
  const url = `${baseAdminPath()}/content/properties/${propertyId}/images`;
  return request(url, {
    method: 'GET',
    signal,
  });
};

export const getPropertyImagesByInternalService = ({
  internalService,
  internalServiceId,
  limit,
  signal,
}: {
  internalService: InternalService;
  internalServiceId: string;
  limit?: number;
  signal?: AbortSignal;
}): Promise<HttpResponse<AdminPropertyImages>> => {
  const url = `${baseAdminPath()}/property-images?${qs.stringify({
    internalService,
    internalServiceId,
    limit,
  })}`;
  return request(url, {
    method: 'GET',
    signal,
  });
};

export const getRoomImagesByInternalService = ({
  internalService,
  internalServiceId,
  limit,
  signal,
}: {
  internalService: InternalService;
  internalServiceId: string;
  limit?: number;
  signal?: AbortSignal;
}): Promise<HttpResponse<AdminRoomImages>> => {
  const url = `${baseAdminPath()}/room-images?${qs.stringify({
    internalService,
    internalServiceId,
    limit,
  })}`;
  return request(url, {
    method: 'GET',
    signal,
  });
};

export const updatePropertyImages = ({
  propertyId,
  images,
}: {
  propertyId: string;
} & UpdatePropertyImagesPayload): Promise<HttpResponse<PropertyImagesResponse>> => {
  const url = `${baseAdminPath()}/content/properties/${propertyId}/images`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ images }),
  });
};

export const dedupePropertyImages = ({
  propertyId,
}: {
  propertyId: string;
}): Promise<HttpResponse<DedupePropertyImagesResponse>> => {
  const url = `${baseAdminPath()}/content/properties/${propertyId}/images/dedupe`;
  return request(url, {
    method: 'POST',
  });
};

export const bulkUpdatePropertyContentSources = (
  { propertyId }: actions['bulkUpdatePropertyContentSources']['parameters']['path'],
  data: BulkUpdatePropertyContentSourcesPayload,
): Promise<HttpResponse<PropertyContentSourcesResponse>> => {
  const url = `${baseAdminPath()}/content/properties/${propertyId}/sources/bulk`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const getRoomContentSources = (
  { roomId }: actions['getRoomContentSources']['parameters']['path'],
  options?: { signal?: AbortSignal },
): Promise<HttpResponse<RoomContentSourcesResponse>> => {
  const url = `${baseAdminPath()}/content/rooms/${roomId}/sources`;
  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
};

export const updateRoomContentSources = (
  { roomId }: actions['updateRoomContentSources']['parameters']['path'],
  data: UpdateRoomContentSourcesPayload,
): Promise<HttpResponse<RoomContentSourcesResponse>> => {
  const url = `${baseAdminPath()}/content/rooms/${roomId}/sources`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

// Bundle configuration (HP-2044) — types derived from contract operations

type GetBundleByIdResponse = actions['getBundleById']['responses']['200']['content']['application/json'];
type GetBundlesResponse = actions['getBundles']['responses']['200']['content']['application/json'];

export type Bundle = GetBundleByIdResponse['result'];
export type BundleOffer = Bundle['offers'][number];
export type BundleHotelRule = Bundle['rules'][number];
export type BundleSchedule = Bundle['schedules'][number];
export type BundleGuestOccupancy = BundleHotelRule['includedGuests'][number];

export type UpdateBundlePricingPayload = actions['putPrice']['requestBody']['content']['application/json'];
export type UpdateBundleRulesPayload = actions['putRule']['requestBody']['content']['application/json'];
export type UpdateBundleContentPayload = actions['putContent']['requestBody']['content']['application/json'];
export type UpdateBundleOfferPayload = actions['putOfferContent']['requestBody']['content']['application/json'];

export const listBundles = ({
  limit,
  page,
  signal,
}: {
  limit?: number;
  page?: number;
  signal?: AbortSignal;
}): Promise<HttpResponse<GetBundlesResponse['result']>> => {
  const url = `${baseAdminPath()}/bundles?${qs.stringify({ limit, page })}`;
  return request(url, { method: 'GET', signal });
};

export const getBundle = ({
  bundleId,
  signal,
}: {
  bundleId: string;
  signal?: AbortSignal;
}): Promise<HttpResponse<Bundle>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}`;
  return request(url, { method: 'GET', signal });
};

export const updateBundlePricing = ({
  bundleId,
  payload,
}: {
  bundleId: string;
  payload: UpdateBundlePricingPayload;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/price`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const updateBundleRules = ({
  bundleId,
  payload,
}: {
  bundleId: string;
  payload: UpdateBundleRulesPayload;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/rule`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const updateBundleContent = ({
  bundleId,
  payload,
}: {
  bundleId: string;
  payload: UpdateBundleContentPayload;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/content`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const updateBundleOffer = ({
  bundleId,
  offerId,
  payload,
}: {
  bundleId: string;
  offerId: string;
  payload: UpdateBundleOfferPayload;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/offers/${offerId}`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

// Bundle CRUD (HP-2379) — atomic create + per-field update + delete

export type CreateBundlePayload = actions['postBundle']['requestBody']['content']['application/json'];
export type CreateBundleResult = actions['postBundle']['responses']['201']['content']['application/json']['result'];
export type UpdateBundlePayload = actions['putBundle']['requestBody']['content']['application/json'];

export const createBundle = ({
  payload,
  errorBodyCallback,
}: {
  payload: CreateBundlePayload;
  errorBodyCallback?: (body: unknown) => void;
}): Promise<HttpResponse<CreateBundleResult>> => {
  const url = `${baseAdminPath()}/bundles`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
    errorBodyCallback,
  });
};

export const updateBundle = ({
  bundleId,
  payload,
  errorBodyCallback,
}: {
  bundleId: string;
  payload: UpdateBundlePayload;
  errorBodyCallback?: (body: unknown) => void;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
    errorBodyCallback,
  });
};

export const deleteBundle = ({ bundleId }: { bundleId: string }): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}`;
  return request(url, { method: 'DELETE' });
};

// Campaign CRUD (HP-2688) — types derived from contract operations

type GetCampaignByIdResponse = actions['getCampaignById']['responses']['200']['content']['application/json'];
type GetCampaignsResponse = actions['getCampaigns']['responses']['200']['content']['application/json'];

export type Campaign = GetCampaignByIdResponse['result'];
export type CampaignSchedule = Campaign['schedules'][number];

export type CreateCampaignPayload = actions['postCampaign']['requestBody']['content']['application/json'];
export type CreateCampaignResult = actions['postCampaign']['responses']['201']['content']['application/json']['result'];
export type UpdateCampaignPayload = actions['putCampaign']['requestBody']['content']['application/json'];

export const listCampaigns = ({
  limit,
  page,
  signal,
}: {
  limit?: number;
  page?: number;
  signal?: AbortSignal;
}): Promise<HttpResponse<GetCampaignsResponse['result']>> => {
  const url = `${baseAdminPath()}/campaigns?${qs.stringify({ limit, page })}`;
  return request(url, { method: 'GET', signal });
};

export const getCampaign = ({
  campaignId,
  signal,
}: {
  campaignId: string;
  signal?: AbortSignal;
}): Promise<HttpResponse<Campaign>> => {
  const url = `${baseAdminPath()}/campaigns/${campaignId}`;
  return request(url, { method: 'GET', signal });
};

export const createCampaign = ({
  payload,
}: {
  payload: CreateCampaignPayload;
}): Promise<HttpResponse<CreateCampaignResult>> => {
  const url = `${baseAdminPath()}/campaigns`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const updateCampaign = ({
  campaignId,
  payload,
}: {
  campaignId: string;
  payload: UpdateCampaignPayload;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/campaigns/${campaignId}`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const deleteCampaign = ({ campaignId }: { campaignId: string }): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/campaigns/${campaignId}`;
  return request(url, { method: 'DELETE' });
};

// Campaign Rules CRUD (svc-accommodation PR #1615) — a rule gates whether a campaign applies to
// a rate, based on shared criteria that will later be reused by bundle hotel rules and dynamic
// pricing. Types are hand-written rather than derived from `actions` because the contract package
// (pinned to 21.5.0) hasn't been bumped to publish these endpoints yet — swap to
// `actions['getCampaignRules']` etc. once it is.

// `RuleCriteria` is intentionally not campaign-specific — it's the shared criteria shape used by
// the `Rule` model regardless of what it's attached to (campaign today; bundle hotel rules and
// dynamic pricing later), so the UI in Pages/Rules built against it can be reused for those too.
export interface RuleCriteria {
  boardCodes?: Array<string>;
  refundableStatuses?: Array<'refundable' | 'partially' | 'non-refundable'>;
  minLengthOfStay?: number;
  minLeadDays?: number;
  ratePlanIds?: Array<string>;
  roomIds?: Array<string>;
  supplierRateIds?: Array<string>;
  suppliers?: Array<string>;
  internalServices?: Array<string>;
  regions?: Array<string>;
  checkInDaysOfWeek?: Array<number>;
  occupancy?: {
    adults?: number;
    children?: number;
    infants?: number;
    minAdults?: number;
    minChildren?: number;
    minInfants?: number;
  };
}

export interface CampaignRule {
  id: string;
  campaignId: string;
  name: string;
  description: string | null;
  criteria: RuleCriteria;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignRulePayload {
  name: string;
  description?: string;
  criteria: RuleCriteria;
}

export interface UpdateCampaignRulePayload {
  name?: string;
  description?: string | null;
  criteria?: RuleCriteria;
}

export const listCampaignRules = ({
  campaignId,
  signal,
}: {
  campaignId: string;
  signal?: AbortSignal;
}): Promise<HttpResponse<{ rules: Array<CampaignRule> }>> => {
  const url = `${baseAdminPath()}/campaigns/${campaignId}/rules`;
  return request(url, { method: 'GET', signal });
};

export const getCampaignRule = ({
  campaignId,
  ruleId,
  signal,
}: {
  campaignId: string;
  ruleId: string;
  signal?: AbortSignal;
}): Promise<HttpResponse<CampaignRule>> => {
  const url = `${baseAdminPath()}/campaigns/${campaignId}/rules/${ruleId}`;
  return request(url, { method: 'GET', signal });
};

export const createCampaignRule = ({
  campaignId,
  payload,
}: {
  campaignId: string;
  payload: CreateCampaignRulePayload;
}): Promise<HttpResponse<{ ruleId: string }>> => {
  const url = `${baseAdminPath()}/campaigns/${campaignId}/rules`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const updateCampaignRule = ({
  campaignId,
  ruleId,
  payload,
}: {
  campaignId: string;
  ruleId: string;
  payload: UpdateCampaignRulePayload;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/campaigns/${campaignId}/rules/${ruleId}`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const deleteCampaignRule = ({
  campaignId,
  ruleId,
}: {
  campaignId: string;
  ruleId: string;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/campaigns/${campaignId}/rules/${ruleId}`;
  return request(url, { method: 'DELETE' });
};

// Experience-level admin (HP-2379)

export type AttachExperiencePayload = actions['postExperience']['requestBody']['content']['application/json'];
export type AttachExperienceResult =
  actions['postExperience']['responses']['201']['content']['application/json']['result'];
export type CandidateTicketsResult =
  actions['getCandidateTickets']['responses']['200']['content']['application/json']['result'];
export type CandidateTicket = CandidateTicketsResult['tickets'][number];

export const attachExperience = ({
  bundleId,
  payload,
}: {
  bundleId: string;
  payload: AttachExperiencePayload;
}): Promise<HttpResponse<AttachExperienceResult>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/experiences`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const getCandidateTickets = ({
  bundleId,
  offerId,
  signal,
}: {
  bundleId: string;
  offerId: string;
  signal?: AbortSignal;
}): Promise<HttpResponse<CandidateTicketsResult>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/experiences/${offerId}/candidate-tickets`;
  return request(url, { method: 'GET', signal });
};

export type EligibleExperiencesResult =
  actions['getEligibleExperiences']['responses']['200']['content']['application/json']['result'];
export type EligibleExperience = EligibleExperiencesResult['experiences'][number];

export const listEligibleExperiences = ({
  search,
  idIn,
  limit,
  offset,
  signal,
}: {
  search?: string;
  idIn?: Array<string>;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<HttpResponse<EligibleExperiencesResult>> => {
  const query = qs.stringify(
    {
      ...(search ? { search } : {}),
      ...(idIn && idIn.length > 0 ? { idIn: idIn.join(',') } : {}),
      ...(limit !== undefined ? { limit } : {}),
      ...(offset !== undefined ? { offset } : {}),
    },
    { addQueryPrefix: true },
  );
  const url = `${baseAdminPath()}/bundles/eligible-experiences${query}`;
  return request(url, { method: 'GET', signal });
};

export const removeExperience = ({
  bundleId,
  offerId,
}: {
  bundleId: string;
  offerId: string;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/experiences/${offerId}`;
  return request(url, { method: 'DELETE' });
};

// Ticket-level admin (HP-2379)

export type CreateTicketPayload = actions['postTicket']['requestBody']['content']['application/json'];
export type CreateTicketResult = actions['postTicket']['responses']['201']['content']['application/json']['result'];
export type UpdateTicketPayload = actions['putTicket']['requestBody']['content']['application/json'];

export const createTicket = ({
  bundleId,
  offerId,
  payload,
}: {
  bundleId: string;
  offerId: string;
  payload: CreateTicketPayload;
}): Promise<HttpResponse<CreateTicketResult>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/experiences/${offerId}/tickets`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const updateTicket = ({
  bundleId,
  offerId,
  ticketId,
  payload,
}: {
  bundleId: string;
  offerId: string;
  ticketId: string;
  payload: UpdateTicketPayload;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/experiences/${offerId}/tickets/${ticketId}`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const removeTicket = ({
  bundleId,
  offerId,
  ticketId,
}: {
  bundleId: string;
  offerId: string;
  ticketId: string;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/bundles/${bundleId}/experiences/${offerId}/tickets/${ticketId}`;
  return request(url, { method: 'DELETE' });
};

// Capabilities (generic per-property feature flags, e.g. LPC/LPP Dynamic Pricing rollout - HP-2812)
export type CapabilityFlagSummary = definitions['AdminCapabilityFlagSummary'];
export type CapabilityPropertyOption = definitions['AdminCapabilityProperty'];
export type CapabilityDetail = definitions['AdminCapabilityHydrated'];

// Static registry (key, label, tooltip description) - no database read on the backend. Drives
// the flag tabs; see getCapability below for a selected flag's subscribed-property table.
export const listCapabilities = (options?: {
  signal?: AbortSignal;
}): Promise<HttpResponse<actions['getCapabilities']['responses']['200']['content']['application/json']['result']>> => {
  const url = `${baseAdminPath()}/capabilities`;
  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
};

export const getCapability = (
  { flag }: actions['getCapability']['parameters']['path'],
  options?: { signal?: AbortSignal },
): Promise<HttpResponse<CapabilityDetail>> => {
  const url = `${baseAdminPath()}/capabilities/${flag}`;
  return request(url, {
    method: 'GET',
    signal: options?.signal,
  });
};

export const subscribePropertyToCapability = ({
  flag,
  propertyId,
}: actions['postCapabilityProperty']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/capabilities/${flag}/properties/${propertyId}`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
  });
};

export const unsubscribePropertyFromCapability = ({
  flag,
  propertyId,
}: actions['deleteCapabilityProperty']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/capabilities/${flag}/properties/${propertyId}`;
  return request(url, { method: 'DELETE' });
};
